import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getBlogPostById, getBlogPostBySlug } from '../data/blogPosts';
import BlogPostViewer from '../components/BlogPostViewer';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    if (slug) {
      // Try to find by slug first, then by numeric ID for backward compatibility
      let post = getBlogPostBySlug(slug);
      if (!post) {
        post = getBlogPostById(slug);
      }
      
      if (post) {
        // Update page title
        document.title = `${post.title} | RealEstart.io - Georgia Real Estate Exam Prep`;
        
        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', post.excerpt);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'description';
          meta.content = post.excerpt;
          document.head.appendChild(meta);
        }

        // Add Open Graph meta tags
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          ogTitle.setAttribute('content', post.title);
        } else {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'og:title');
          meta.content = post.title;
          document.head.appendChild(meta);
        }

        const ogDescription = document.querySelector('meta[property="og:description"]');
        if (ogDescription) {
          ogDescription.setAttribute('content', post.excerpt);
        } else {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'og:description');
          meta.content = post.excerpt;
          document.head.appendChild(meta);
        }

        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) {
          ogUrl.setAttribute('content', `${window.location.origin}${post.link}`);
        } else {
          const meta = document.createElement('meta');
          meta.setAttribute('property', 'og:url');
          meta.content = `${window.location.origin}${post.link}`;
          document.head.appendChild(meta);
        }

        // Add Twitter Card meta tags
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        if (twitterTitle) {
          twitterTitle.setAttribute('content', post.title);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'twitter:title';
          meta.content = post.title;
          document.head.appendChild(meta);
        }

        const twitterDescription = document.querySelector('meta[name="twitter:description"]');
        if (twitterDescription) {
          twitterDescription.setAttribute('content', post.excerpt);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'twitter:description';
          meta.content = post.excerpt;
          document.head.appendChild(meta);
        }

        // Add canonical URL
        const canonicalUrl = document.querySelector('link[rel="canonical"]');
        if (canonicalUrl) {
          canonicalUrl.setAttribute('href', `${window.location.origin}${post.link}`);
        } else {
          const link = document.createElement('link');
          link.rel = 'canonical';
          link.href = `${window.location.origin}${post.link}`;
          document.head.appendChild(link);
        }

        // Add structured data for Google
        const structuredData = {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "author": {
            "@type": "Organization",
            "name": post.author
          },
          "datePublished": post.date,
          "dateModified": post.date,
          "publisher": {
            "@type": "Organization",
            "name": "RealEstart.io",
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo192.png`
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${window.location.origin}${post.link}`
          }
        };

        // Remove existing structured data
        const existingScript = document.querySelector('script[type="application/ld+json"]');
        if (existingScript) {
          existingScript.remove();
        }

        // Add new structured data
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    }
  }, [slug]);

  if (!slug) {
    return (
      <div className="min-h-screen py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4 text-gray-900">
              Blog Post Not Found
            </h1>
            <p className="text-xl mb-8 text-gray-600">
              No blog post slug provided.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <BlogPostViewer postId={slug} />;
};

export default BlogPost;
